import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import { DisciplineActivationRunType, DisciplineScheduleFlow } from './enums'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleAllocateApi } from '../api/schedule-allocate.hook'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'
import { useDisciplineScheduleResults } from './discipline-schedule-results.hook'
import { getScheduleStartMinDate } from './discipline-schedule'

interface ModalDisciplineScheduleFiltersReturn {
  refGender: WritableComputedRef<string[]>
  refFeatures: WritableComputedRef<string[]>
  refIsPremium: ComputedRef<boolean>
  refHasPremiumHint: ComputedRef<boolean>
  refGenderOptions: ComputedRef<AppSelectOption<string>[]>
  refFeatureOptions: ComputedRef<AppSelectOption<string>[]>
  refIsLoading: ComputedRef<boolean>
  togglePremium: () => void
  close: () => void
  back: () => void
  confirm: () => Promise<void>
}

/** The grade feature is not a filter of the tutor search. */
const EXCLUDED_FEATURE = 'GRADE'

const toOptions = (list: Record<string, string>): AppSelectOption<string>[] =>
  Object.entries(list).map(([value, text]) => ({ value, text }))

/**
 * Continue starts the tutor search for the picked hours. Change tutor has no start-date step: its search starts on
 * the earliest day the API accepts.
 */
export const useModalDisciplineScheduleFilters = (): ModalDisciplineScheduleFiltersReturn => {
  const toast = useToast()
  const store = useDisciplineScheduleStore()
  const listsStore = useListsStore()
  const { refDetails, refDisciplineId, customerNow, close, back } = useDisciplineScheduleContext()
  const { loadResults } = useDisciplineScheduleResults()

  const refGender = computed<string[]>({
    get: () => store.getFilters.gender,
    set: (value) => {
      store.patchFilters({ gender: value })
    },
  })

  const refFeatures = computed<string[]>({
    get: () => store.getFilters.special_features,
    set: (value) => {
      store.patchFilters({ special_features: value })
    },
  })

  const refIsPremium = computed<boolean>(() => store.getFilters.premium)
  const refHasPremiumHint = computed<boolean>(() => !!refDetails.value?.has_premium_subscription)

  const refGenderOptions = computed<AppSelectOption<string>[]>(() => toOptions(listsStore.getLists?.tutor.gender ?? {}))

  const refFeatureOptions = computed<AppSelectOption<string>[]>(() =>
    toOptions(listsStore.getLists?.tutor.special_feature ?? {}).filter(option => option.value !== EXCLUDED_FEATURE))

  const refIsLoading = computed<boolean>(() => store.isAllocating)

  const togglePremium = (): void => {
    store.patchFilters({ premium: !store.getFilters.premium })
  }

  const confirm = async (): Promise<void> => {
    if (store.isAllocating) return

    const isChangeTutor = store.getFlow === DisciplineScheduleFlow.ChangeTutor
    const startDate = isChangeTutor ? getScheduleStartMinDate(customerNow()) : store.getStartDate

    if (!startDate || !store.getSelected.length) {
      toast.warning({ title: 'Warning!', description: 'Data is null!' })
      return
    }

    store.setAllocating(true)

    const run = await useDisciplineScheduleAllocateApi({
      disciplineId: refDisciplineId.value,
      payload: {
        schedule: store.getSelected.map(slot => ({ day: slot.day, from: slot.start_time })),
        start_date: startDate,
        type: isChangeTutor ? DisciplineActivationRunType.ChangeTutor : DisciplineActivationRunType.Manual,
        ...(store.getPace !== null && { pace: store.getPace }),
        tutor_filters: { ...store.getFilters },
      },
    })

    store.setAllocating(false)
    if (!run) return

    store.setActivationRunId(run.id)
    store.nextStep()

    if (store.isRunDone(run.id)) await loadResults(run.id)
  }

  return {
    refGender,
    refFeatures,
    refIsPremium,
    refHasPremiumHint,
    refGenderOptions,
    refFeatureOptions,
    refIsLoading,
    togglePremium,
    close,
    back,
    confirm,
  }
}
